import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

/**
 * Reusable report dialog. Knows nothing about any specific report.
 *
 * title / subtitle — header text
 * loading / error  — state; error shows a Retry button when onRetry is given
 * actions          — node rendered in header (Print button, Export, ...)
 * children         — report body
 * zIndex           — above the chat widget (1300)
 */
export default function ReportDialog({
  open,
  onClose,
  title,
  subtitle,
  loading = false,
  error = "",
  onRetry,
  actions = null,
  maxWidth = "lg",
  accentColor = "#2563eb",
  primaryColor = "#0f172a",
  zIndex = 1500,
  children,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      scroll="paper"
      sx={{ zIndex }}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, height: fullScreen ? "100%" : "88vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          color: "#fff",
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
          py: 1.75,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap component="div">
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: "#fff" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", minHeight: 240 }}>
            <CircularProgress size={32} />
          </Stack>
        ) : error ? (
          <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ height: "100%", minHeight: 240, px: 2 }}>
            <Typography color="error" align="center">
              {error}
            </Typography>
            {onRetry && (
              <Button variant="outlined" onClick={onRetry}>
                Retry
              </Button>
            )}
          </Stack>
        ) : (
          children
        )}
      </DialogContent>
    </Dialog>
  );
}
